import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import type { LanguageBinding } from "@/hooks/useLanguage";
import type { ThemeBinding } from "@/hooks/useTheme";
import { cx } from "@/lib/classnames";

const ITEMS = [
  { k: "home", path: "/" },
  { k: "book", path: "/book" },
  { k: "concepts", path: "/concepts" },
  { k: "videos", path: "/videos" },
  { k: "posts", path: "/posts" },
  { k: "english", path: "/english" },
  { k: "about", path: "/about" },
  { k: "legacy", path: "/legacy" },
] as const;

type NavKey = (typeof ITEMS)[number]["k"];

export const Nav: React.FC<{
  language: LanguageBinding;
  theme: ThemeBinding;
}> = ({ language, theme }) => {
  const { t, lang, isRTL, toggle } = language;
  const [open, setOpen] = useState(false);
  const navFont = isRTL ? "ff-ar-sans" : "ff-en-sc";
  const location = useLocation();

  // Sun/moon glyph swaps per mode; click toggles theme. Label is bilingual
  // for AT compatibility.
  const themeLabel = theme.isDark
    ? isRTL ? "تفعيل الوضع الفاتح" : "Switch to light mode"
    : isRTL ? "تفعيل الوضع الداكن" : "Switch to dark mode";
  const ThemeIcon = theme.isDark ? (
    // sun (clicking this switches to light)
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <circle cx="10" cy="10" r="3.5" />
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" />
    </svg>
  ) : (
    // moon (clicking this switches to dark)
    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path d="M14.5 13.5a6 6 0 0 1-8-8 6.5 6.5 0 1 0 8 8Z" />
    </svg>
  );

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{
        background: "var(--nav-bg)",
        color: "var(--nav-text)",
        borderBottom: "1px solid var(--hairline-dark)",
      }}
    >
      <div className="max-w-page mx-auto px-6 md:px-10 py-4 flex items-center gap-6">
        <Link
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-3 group"
          aria-label={t.site.name}
        >
          <div
            className="h-10 w-10 rounded-full overflow-hidden shrink-0"
            style={{
              boxShadow:
                "0 0 0 1px var(--vermillion), 0 0 0 3px rgba(200,68,30,.15)",
            }}
          >
            <img
              src={`${import.meta.env.BASE_URL}author/avatar.jpg`}
              alt={t.site.name}
              width={40}
              height={40}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="text-start" style={{ color: "var(--parchment)" }}>
            <div
              className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-lg leading-none")}
              style={{ fontWeight: 600 }}
            >
              {t.site.name}
            </div>
            <div
              className={cx(
                "mt-1",
                isRTL ? "ff-ar-sans text-[11px]" : "ff-en-sc tracked-wide text-[10px]",
                "opacity-60",
              )}
            >
              {t.site.tagline}
            </div>
          </div>
        </Link>

        <div className="flex-1" />

        <nav
          className={cx("hidden lg:flex items-center gap-7", navFont)}
          style={{ color: "var(--parchment)" }}
          aria-label="Primary"
        >
          {ITEMS.map((it) => {
            const label = t.nav[it.k as NavKey];
            const active = location.pathname === it.path;
            return (
              <NavLink
                key={it.k}
                to={it.path}
                end={it.path === "/"}
                className={cx(
                  "link-ul text-[13px]",
                  !isRTL && "tracked-wide",
                  "transition-opacity",
                  active ? "opacity-100" : "opacity-70 hover:opacity-100",
                )}
                style={active ? { color: "var(--vermillion-2)" } : undefined}
              >
                {label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={theme.toggle}
            className="h-9 w-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
            style={{
              color: "var(--parchment)",
              border: "1px solid var(--hairline-dark)",
            }}
            aria-label={themeLabel}
            title={themeLabel}
          >
            {ThemeIcon}
          </button>
          <button
            onClick={toggle}
            className={cx(
              lang === "ar" ? "ff-en-sc" : "ff-ar-sans",
              "text-[12px] tracked-wide px-3 py-2 rounded-full",
            )}
            style={{ color: "var(--parchment)", border: "1px solid var(--hairline-dark)" }}
            aria-label="Toggle language"
          >
            {t.langLabel}
          </button>
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
              "text-[13px] md:text-[14px] px-4 py-2.5 rounded-full transition-all hover:brightness-110",
            )}
            style={{ background: "var(--vermillion)", color: "#fff3df" }}
          >
            {t.nav.contact}
          </Link>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          style={{ color: "var(--parchment)" }}
        >
          <svg width="28" height="20" viewBox="0 0 28 20" fill="none" stroke="currentColor" strokeWidth="1.6">
            {open ? (
              <>
                <line x1="4" y1="4" x2="24" y2="16" />
                <line x1="24" y1="4" x2="4" y2="16" />
              </>
            ) : (
              <>
                <line x1="2" y1="4" x2="26" y2="4" />
                <line x1="2" y1="10" x2="26" y2="10" />
                <line x1="2" y1="16" x2="26" y2="16" />
              </>
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t"
          style={{ borderColor: "var(--hairline-dark)", background: "var(--nav-bg)" }}
        >
          <div className="max-w-page mx-auto px-6 py-6 flex flex-col gap-4">
            {ITEMS.map((it) => {
              const label = t.nav[it.k as NavKey];
              const active = location.pathname === it.path;
              return (
                <NavLink
                  key={it.k}
                  to={it.path}
                  end={it.path === "/"}
                  onClick={() => setOpen(false)}
                  className={cx(
                    "text-start text-base",
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                  )}
                  style={{ color: active ? "var(--vermillion-2)" : "var(--parchment)" }}
                >
                  {label}
                </NavLink>
              );
            })}
            <div
              className="flex items-center gap-3 pt-3"
              style={{ borderTop: "1px solid var(--hairline-dark)" }}
            >
              <button
                onClick={theme.toggle}
                className="h-9 w-9 rounded-full flex items-center justify-center"
                style={{ color: "var(--parchment)", border: "1px solid var(--hairline-dark)" }}
                aria-label={themeLabel}
              >
                {ThemeIcon}
              </button>
              <button
                onClick={toggle}
                className="text-[13px] px-3 py-2 rounded-full"
                style={{ color: "var(--parchment)", border: "1px solid var(--hairline-dark)" }}
              >
                {t.langLabel}
              </button>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className={cx(
                  "flex-1 text-center",
                  isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                  "text-[14px] px-4 py-2.5 rounded-full",
                )}
                style={{ background: "var(--vermillion)", color: "#fff3df" }}
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
